import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { COMMON_BENEFITS } from '../../lib/constants';
import PesoLayout from './PesoLayout';
import TopBar from '../../components/TopBar';
import { Field, Txt, Sel, RadioRow, CheckGroup, SectionCard } from './PesoFormControls';
import { useOffice } from './officeCtxRef';
import {
  EMPLOYER_TYPES, WORK_FORCE_BUCKETS, WORK_FORCE_TO_BT, NATURE_OF_WORK,
  SEX_PREF, CIVIL_STATUS_PREF, ACCEPTS_DISABILITY_TYPES, ACCEPTS_PROGRAMS, TITLE_OPTIONS,
  NSRP_HIGHEST_EDUCATION,
} from './pesoFields';
import {
  uploadImageAuthed, postPesoVacancy, applyEmployerMeta, stashPendingVacancy,
} from './pesoVacancy';

const FileField = ({ label, file, onPick, accept = 'image/*', hint }) => (
  <Field label={label} hint={hint}>
    {file ? (
      <div className="flex items-center gap-2 text-sm text-slate-700">
        <span className="truncate">{file.name}</span>
        <button type="button" onClick={() => onPick(null)} className="text-xs text-red-500">Remove</button>
      </div>
    ) : (
      <input type="file" accept={accept} onChange={(e) => onPick(e.target.files?.[0] || null)} className="text-sm" />
    )}
  </Field>
);

const YES_NO = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
const OTHERS_SPECIFY = 'Others (please specify)';

// The official Form 2 certification/authorization wording (source typos corrected:
// "authorize", "Field Offices", "access") + the platform DPA/MOA consent line.
const CERT_FALLBACK =
  'I certify that all data/information provided in this form are true to the best of my knowledge. ' +
  'I authorize the DOLE to include the establishment profile in the PESO Employment Information System (PEIS). ' +
  'I understand that the establishment profile and contact details shall be made available to jobseekers, PESOs, ' +
  'DOLE Regional and Field Offices, the Bureau of Local Employment, and others who have access to the PEIS, and ' +
  'that DOLE is not obliged to seek applicants on our behalf. I also consent to PESO Panglao and Bohol Trabaho ' +
  'processing this information under the Data Privacy Act of 2012 and the LGU–Bohol Trabaho MOA.';

const eduLabel = (v) => (NSRP_HIGHEST_EDUCATION.find((o) => o.value === v) || {}).label || v || '';

// PESO vacancies can be paid daily or monthly; each has a realistic minimum salary floor
// (blocks token "1 peso" postings that hide the real pay).
const SALARY_PERIODS = [{ value: 'monthly', label: 'Monthly' }, { value: 'daily', label: 'Daily' }];
const SALARY_FLOOR = { monthly: 1000, daily: 100 };

const PesoEmployerRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const office = useOffice();
  const [searchParams] = useSearchParams();
  const [eventInfo, setEventInfo] = useState(null); // { valid, code, name } | null when the ?event= link is valid
  useEffect(() => {
    const code = (searchParams.get('event') || '').trim().toLowerCase();
    const slug = office?.pesoOffice || office?.slug;
    if (!code || !slug) { setEventInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/peso/event-info', { params: { office: slug, code } });
        if (!cancelled) setEventInfo(res.data?.valid ? res.data : null);
      } catch { if (!cancelled) setEventInfo(null); }
    })();
    return () => { cancelled = true; };
  }, [searchParams, office]);
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [posterFile, setPosterFile] = useState(null);

  const [f, setF] = useState({
    // Section I — Establishment Details
    business_name: '', acronym: '', tin: '', employer_type: '', work_force: '',
    line_of_business: '', address: '', municipality: '', barangay: '', province: 'Bohol',
    // Section II — Establishment Contact Details
    contact_title: '', contact_title_other: '', contact_person: '', position: '',
    telephone: '', business_phone: '', fax: '', email: '', password: '',
    // Section III — Vacancy Details
    title: '', panglao_category: '', description: '', nature_value: '', place_of_work: '',
    salary_min: '', salary_max: '', salary_note: '', salary_period: 'monthly', positions_available: '1',
    // Section IV — Qualification Requirements
    work_experience_months: '', religion: '', sex_pref: '', civil_status_pref: '',
    other_qualifications: '', accepts_disability: '', accepts_disability_types: [], accepts_disability_other: '',
    educational_level: '', course_major: '', license: '', eligibility: '', certification: '',
    languages_spoken: '', preferred_residence: '', accepts_programs: [],
    // Section V — Posting Details
    posting_date: new Date().toISOString().slice(0, 10), valid_until: '',
    // Bohol Trabaho extras (not part of the official form)
    facebook: '', business_description: '', benefits: [],
    consent: false,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Default the location municipality to this LGU office (editable), once office loads.
  useEffect(() => {
    if (office?.municipality) {
      setF((prev) => (prev.municipality ? prev : { ...prev, municipality: office.municipality }));
    }
  }, [office?.municipality]);

  // Canonical NSRP Form 2 payload — the SINGLE producer used for both persistence cohorts.
  const buildNsrpData = () => ({
    form: 'nsrp_form_2',
    captured_at: new Date().toISOString(),
    establishment: {
      name: f.business_name, acronym: f.acronym, tin: f.tin, employer_type: f.employer_type,
      work_force: f.work_force, line_of_business: f.line_of_business, address: f.address,
      barangay: f.barangay, municipality: f.municipality, province: f.province,
    },
    contact: {
      title: f.contact_title, title_other: f.contact_title_other, contact_person: f.contact_person,
      position: f.position, telephone: f.telephone, mobile: f.business_phone, fax: f.fax, email: f.email,
    },
    vacancy: {
      position_title: f.title,
      job_description: f.description,
      nature_of_work: (NATURE_OF_WORK.find((o) => o.value === f.nature_value) || {}).label || '',
      place_of_work: f.place_of_work, salary_min: f.salary_min, salary_max: f.salary_max, salary_period: f.salary_period,
      salary_note: f.salary_note, vacancy_count: f.positions_available, panglao_category: f.panglao_category,
    },
    requirements: {
      work_experience_months: f.work_experience_months, religion: f.religion, sex: f.sex_pref,
      civil_status: f.civil_status_pref, other_qualifications: f.other_qualifications,
      accepts_disability: f.accepts_disability, accepts_disability_types: f.accepts_disability_types,
      accepts_disability_other: f.accepts_disability_other, educational_level: eduLabel(f.educational_level),
      course_major: f.course_major, license: f.license, eligibility: f.eligibility,
      certification: f.certification, languages_spoken: f.languages_spoken,
      preferred_residence: f.preferred_residence, accepts_programs: f.accepts_programs,
    },
    posting: { posting_date: f.posting_date, valid_until: f.valid_until },
  });

  const buildVacancyObject = (nd) => {
    const location = [f.place_of_work, f.barangay, f.municipality, f.province].filter(Boolean).join(', ');
    const how = [
      f.email ? `Email: ${f.email}` : '',
      f.business_phone ? `Contact: ${f.business_phone}` : '',
    ].filter(Boolean).join(' · ');
    let expires_at;
    if (f.valid_until) {
      const d = new Date(f.valid_until);
      if (!isNaN(d.getTime())) expires_at = d.toISOString();
    }
    return {
      title: f.title.trim(),
      panglao_category: f.panglao_category,
      bt_category: (office?.categoryMap && office.categoryMap[f.panglao_category]) || 'Other',
      positions_available: f.positions_available,
      description: f.description,
      nature_of_work: nd.vacancy.nature_of_work,   // verbatim label (printed / description)
      nature_value: f.nature_value,                // stable slug (drives NATURE_TO_JOB)
      requirements: nd.requirements,               // structured Section IV for buildQualificationsText
      salary_min: f.salary_min, salary_max: f.salary_max, salary_period: f.salary_period,
      salary_note: f.salary_note,
      benefits: f.benefits,
      how_to_apply: how,
      location,
      expires_at,
    };
  };

  const handleSubmit = async () => {
    // Single-page form: validate per section with a clear message pointing at the gap.
    if (!(f.business_name.trim() && f.employer_type && f.municipality.trim())) {
      toast.error(t('peso.fillEstablishment', 'Please complete Section I: establishment name, employer type, and city/municipality.'));
      return;
    }
    if (!(f.contact_person.trim() && f.business_phone.trim() && /\S+@\S+\.\S+/.test(f.email) && f.password.length >= 8)) {
      toast.error(t('peso.fillContact', 'Please complete Section II: contact person, mobile number, a valid email, and a password (8+ characters).'));
      return;
    }
    if (!(f.title.trim().length >= 3 && f.panglao_category && f.description.trim().length >= 20 && f.nature_value && f.positions_available)) {
      toast.error(t('peso.fillVacancy2', 'Please complete Section III: position title, job category, a description (20+ characters), nature of work, and vacancy count.'));
      return;
    }
    // Salary is mandatory. Enforce the per-period floor (daily ₱100, monthly ₱1,000) so
    // employers can't hide the real pay with a token "1 peso" value.
    const salaryFloor = SALARY_FLOOR[f.salary_period] || 1000;
    const salaryPer = f.salary_period === 'daily' ? 'day' : 'month';
    const sMin = Math.floor(Number(f.salary_min));
    if (!f.salary_min || isNaN(sMin) || sMin < salaryFloor) {
      toast.error(t('peso.fillSalary', `Please enter a salary of at least ₱${salaryFloor.toLocaleString()} per ${salaryPer}. Enter the real salary — it cannot be left blank.`));
      return;
    }
    if (f.salary_max) {
      const sMax = Math.floor(Number(f.salary_max));
      if (!isNaN(sMax) && sMax > 0 && sMax < sMin) {
        toast.error(t('peso.salaryMaxLow', 'Maximum salary cannot be less than the minimum salary.'));
        return;
      }
    }
    if (!f.accepts_disability) {
      toast.error(t('peso.fillQualifications', 'Please answer "Accepts Disability" (Yes or No) in Section IV.'));
      return;
    }
    // Job-post dates must be today or in the future — never in the past (ISO YYYY-MM-DD
    // strings compare chronologically). Posting date defaults to today.
    const todayStr = new Date().toISOString().slice(0, 10);
    if (f.posting_date && f.posting_date < todayStr) {
      toast.error(t('peso.postingDatePast', 'The posting date cannot be in the past. Please use today or a future date.'));
      return;
    }
    if (f.valid_until) {
      if (f.valid_until < todayStr) {
        toast.error(t('peso.validUntilPast', 'The "Valid Until" date cannot be in the past. Please use today or a future date.'));
        return;
      }
      if (f.posting_date && f.valid_until < f.posting_date) {
        toast.error(t('peso.validUntilBeforePosting', 'The "Valid Until" date cannot be before the posting date.'));
        return;
      }
    }
    if (!f.consent) { toast.error(t('peso.consentRequired', 'Please confirm the certification & consent.')); return; }
    setSubmitting(true);
    try {
      const nd = buildNsrpData();
      const btCategory = (office?.categoryMap && office.categoryMap[f.panglao_category]) || 'Other';
      const numEmployees = WORK_FORCE_TO_BT[f.work_force] || '';
      const metadata = {
        name: f.business_name,
        role: 'employer',
        town: f.municipality || office?.municipality || '',
        business_name: f.business_name,
        business_phone: f.business_phone,
        business_description: f.business_description || undefined,
        is_private_household: false,
        industry: btCategory,
        business_type: 'Establishment',
        employee_count: numEmployees,
        referred_by: office?.referralCode,
        terms_agreed_at: new Date().toISOString(),
        registration_source: office?.registrationSource,
        peso_office: office?.pesoOffice,
        event_code: eventInfo?.code || undefined,
        // Full NSRP Form 2 payload as a JSON STRING — the handle_new_user trigger casts
        // (raw_user_meta_data->>'nsrp_data')::jsonb, so the email-confirm cohort (no session)
        // still gets every field persisted.
        nsrp_data: JSON.stringify(nd),
      };

      const { data: authData, error } = await supabase.auth.signUp({
        email: f.email, password: f.password,
        options: { data: metadata, emailRedirectTo: `${window.location.origin}/auth/callback` },
      });
      if (error) throw error;

      const vacancy = buildVacancyObject(nd);
      const employerMeta = { facebook: f.facebook, business_description: f.business_description };

      if (authData?.session) {
        const token = authData.session.access_token;
        const employerId = authData.session.user.id;

        // Create the profile row explicitly (mirrors Auth.js). The handle_new_user trigger also
        // creates it from metadata; this upsert is idempotent (ON CONFLICT DO UPDATE) and
        // guarantees the row exists before the authenticated PUT below enriches nsrp_data.
        try {
          await supabase.from('profiles').upsert({
            id: employerId,
            auth_user_id: employerId,
            email: authData.session.user.email,
            name: metadata.name,
            role: 'employer',
            town: metadata.town,
            business_name: metadata.business_name,
            business_phone: metadata.business_phone,
            business_description: metadata.business_description || null,
            is_private_household: false,
            industry: metadata.industry,
            business_type: metadata.business_type,
            number_of_employees: numEmployees,
            referred_by: metadata.referred_by,
            terms_agreed_at: metadata.terms_agreed_at,
            registration_source: metadata.registration_source,
            peso_office: metadata.peso_office,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }, { onConflict: 'id', ignoreDuplicates: false });
        } catch (e) { console.warn('[PESO] employer profile create failed (non-fatal):', e); }

        // Persist the full Form 2 payload via the sanitized backend writer (the authoritative
        // nsrp_data write for the session cohort). MUST complete before the redirect.
        try {
          await api.put('/api/profile', { nsrp_data: nd }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) {
          console.warn('[PESO] employer nsrp_data PUT failed:', e);
          toast.error(t('peso.profileSaveWarn', 'Account created, but some registration details may need to be re-entered from your dashboard.'));
        }

        try {
          if (logoFile) employerMeta.avatar_url = await uploadImageAuthed(logoFile, token);
        } catch (e) { console.warn('[PESO] logo upload failed (non-fatal):', e); }
        try {
          if (posterFile) vacancy.image_url = await uploadImageAuthed(posterFile, token);
        } catch (e) { console.warn('[PESO] poster upload failed (non-fatal):', e); }

        try { await applyEmployerMeta(employerId, employerMeta, token); } catch (e) { console.warn('[PESO] employer meta failed (non-fatal):', e); }

        try { localStorage.removeItem('referral_code'); } catch {}

        // Save the vacancy as a DRAFT pending PESO-staff approval. On any transient failure
        // (incl. the null-category guard), stash it so the dashboard retries.
        try {
          await postPesoVacancy(vacancy, token);
          toast.success(t('peso.employerPending', 'Account created! Your vacancy is saved and pending PESO approval.'));
        } catch (e) {
          stashPendingVacancy({ vacancy, employerMeta });
          toast.success(t('peso.employerSaved', 'Account created! Your vacancy was saved and will be submitted for PESO approval shortly.'));
        }
        // Soft SPA navigation (NOT a hard reload): a hard window.location reload right after
        // signup drops the fresh session inside mobile in-app WebViews (Facebook/Safari), booting
        // the user to the login page. Staying in the SPA keeps the in-memory session; App.js
        // already has the signed-in user and self-heals the profile.
        navigate('/dashboard', { replace: true });
      } else {
        // Email confirmation required: the trigger persists nsrp_data + employee_count from metadata.
        // Stash the vacancy (text-only) + branding; it is posted on first login by the dashboard.
        stashPendingVacancy({ vacancy, employerMeta });
        try { localStorage.removeItem('referral_code'); } catch {}
        toast.success(t('peso.employerCheckEmail', 'Account created! Confirm your email, then log in to finish posting your vacancy.'));
        navigate(`/check-email?email=${encodeURIComponent(f.email)}`);
      }
    } catch (err) {
      const msg = err?.message || err?.response?.data?.detail || t('peso.signupFailed', 'Registration failed. Please try again.');
      toast.error(typeof msg === 'string' ? msg : 'Registration failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const showTitleOther = f.contact_title === OTHERS_SPECIFY;
  const showDisabilityTypes = f.accepts_disability === 'yes';
  const showDisabilityOther = showDisabilityTypes && f.accepts_disability_types.includes(OTHERS_SPECIFY);
  const notOfficial = t('peso.notOfficialForm', 'Not part of the official NSRP form');
  const salaryFloor = SALARY_FLOOR[f.salary_period] || 1000;
  const salaryPer = f.salary_period === 'daily' ? 'day' : 'month';
  const todayStr = new Date().toISOString().slice(0, 10); // job-post dates can't be in the past

  return (
    <>
      <TopBar />
      <PesoLayout>
      <Helmet><title>Employer Registration | PESO Panglao</title></Helmet>

      <div className="mb-5">
        <button onClick={() => navigate(`/peso/${office?.slug || 'panglao'}`)} className="mb-3 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
          <ArrowLeft size={14} /> {t('peso.backToPortal', 'Back to portal')}
        </button>
        <h1 className="text-xl font-bold text-slate-900">{t('peso.employerRegTitle', 'Employer Registration')}</h1>
        <p className="text-sm text-slate-500">{t('peso.employerRegSubtitle', 'NSRP Establishment Registration Form. Fields marked * are required.')}</p>
      </div>

      {eventInfo?.valid && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          {t('peso.registeringForEvent', 'Registering for')}: <span className="font-semibold">{eventInfo.name}</span>
        </div>
      )}

      <div className="space-y-4">
        {/* I. Establishment Details */}
        <SectionCard title={t('peso.secEstablishment', 'I. Establishment Details')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('peso.companyName', 'Establishment Name')} required><Txt value={f.business_name} onChange={(v) => set('business_name', v)} /></Field>
            <Field label={t('peso.acronym', 'Acronym/Abbreviation')}><Txt value={f.acronym} onChange={(v) => set('acronym', v)} /></Field>
            <Field label={t('peso.tin', 'Tax Identification Number')}><Txt value={f.tin} onChange={(v) => set('tin', v)} /></Field>
            <div className="sm:col-span-2">
              <Field label={t('peso.employerType', 'Employer type (check only 1)')} required>
                <RadioRow name="emptype" value={f.employer_type} onChange={(v) => set('employer_type', v)} options={EMPLOYER_TYPES} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label={t('peso.totalWorkForce', 'Total Work Force')}>
                <RadioRow name="workforce" value={f.work_force} onChange={(v) => set('work_force', v)} options={WORK_FORCE_BUCKETS} />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label={t('peso.lineOfBusiness', 'Line of Business/Industry (check BIR 2303)')}><Txt value={f.line_of_business} onChange={(v) => set('line_of_business', v)} /></Field>
            </div>
            <Field label={t('peso.address', 'Address')}><Txt value={f.address} onChange={(v) => set('address', v)} /></Field>
            <Field label={t('peso.barangay', 'Barangay')}>
              {(office?.barangays?.length && f.municipality === office.municipality)
                ? <Sel value={f.barangay} onChange={(v) => set('barangay', v)} options={office.barangays} />
                : <Txt value={f.barangay} onChange={(v) => set('barangay', v)} />}
            </Field>
            <Field label={t('peso.municipality', 'City/Municipality')} required><Txt value={f.municipality} onChange={(v) => set('municipality', v)} /></Field>
            <Field label={t('peso.province', 'Province')}><Txt value={f.province} onChange={(v) => set('province', v)} /></Field>
          </div>
        </SectionCard>

        {/* II. Establishment Contact Details */}
        <SectionCard title={t('peso.secContact', 'II. Establishment Contact Details')}>
          <Field label={t('peso.title', 'Title')}>
            <RadioRow name="title" value={f.contact_title} onChange={(v) => set('contact_title', v)} options={TITLE_OPTIONS} />
          </Field>
          {showTitleOther && (
            <Field label={t('peso.titleOther', 'Others (please specify)')}><Txt value={f.contact_title_other} onChange={(v) => set('contact_title_other', v)} /></Field>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('peso.contactPerson', 'Contact Person (Full name)')} required><Txt value={f.contact_person} onChange={(v) => set('contact_person', v)} /></Field>
            <Field label={t('peso.position', 'Position')}><Txt value={f.position} onChange={(v) => set('position', v)} /></Field>
            <Field label={t('peso.telephone', 'Telephone No')}><Txt value={f.telephone} onChange={(v) => set('telephone', v)} /></Field>
            <Field label={t('peso.mobileNo', 'Mobile No')} required><Txt value={f.business_phone} onChange={(v) => set('business_phone', v)} placeholder="09XXXXXXXXX" /></Field>
            <Field label={t('peso.faxNo', 'Fax No')}><Txt value={f.fax} onChange={(v) => set('fax', v)} /></Field>
            <Field label={t('peso.designatedEmail', 'Email Address')} required hint={t('peso.designatedEmailHint', 'This becomes your login. Gmail preferred.')}>
              <Txt type="email" value={f.email} onChange={(v) => set('email', v)} />
            </Field>
            <Field label={t('peso.password', 'Password')} required hint={`${t('peso.passwordHint', 'At least 8 characters')} · ${notOfficial}`}>
              <Txt type="password" value={f.password} onChange={(v) => set('password', v)} />
            </Field>
          </div>
        </SectionCard>

        {/* III. Vacancy Details */}
        <SectionCard title={t('peso.secVacancy', 'III. Vacancy Details')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('peso.jobTitle', 'Position Title')} required><Txt value={f.title} onChange={(v) => set('title', v)} /></Field>
            <Field label={t('peso.jobCategory', 'Job Category')} required hint={notOfficial}><Sel value={f.panglao_category} onChange={(v) => set('panglao_category', v)} options={office?.categoryLabels || []} /></Field>
          </div>
          <Field label={t('peso.jobDescription', 'Job Description')} required hint={t('peso.jobDescHint', 'List 2–3 main responsibilities')}>
            <textarea value={f.description} onChange={(e) => set('description', e.target.value)} rows={3}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </Field>
          <Field label={t('peso.natureOfWork', 'Nature of Work')} required>
            <RadioRow name="nature" value={f.nature_value} onChange={(v) => set('nature_value', v)} options={NATURE_OF_WORK} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('peso.placeOfWork', 'Place of Work')}><Txt value={f.place_of_work} onChange={(v) => set('place_of_work', v)} /></Field>
            <Field label={t('peso.vacancyCount', 'Vacancy Count')} required><Txt type="number" min="1" value={f.positions_available} onChange={(v) => set('positions_available', v)} /></Field>
          </div>
          <Field label={t('peso.salaryPeriod', 'Salary Period')} required>
            <RadioRow name="salaryperiod" value={f.salary_period} onChange={(v) => set('salary_period', v)} options={SALARY_PERIODS} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={`${t('peso.salaryMinLabel', 'Salary min')} (₱/${salaryPer})`} required hint={`${t('peso.salaryAtLeast', 'At least')} ₱${salaryFloor.toLocaleString()}/${salaryPer} — ${t('peso.enterRealSalary', 'enter the real salary')}`}><Txt type="number" value={f.salary_min} onChange={(v) => set('salary_min', v)} /></Field>
            <Field label={`${t('peso.salaryMaxLabel', 'Salary max')} (₱/${salaryPer}, ${t('peso.optionalWord', 'optional')})`}><Txt type="number" value={f.salary_max} onChange={(v) => set('salary_max', v)} /></Field>
          </div>
          <Field label={t('peso.salaryNote', 'Salary note (optional)')} hint={t('peso.salaryNoteHint', 'e.g. based on the regional minimum wage')}><Txt value={f.salary_note} onChange={(v) => set('salary_note', v)} /></Field>
        </SectionCard>

        {/* IV. Qualification Requirements */}
        <SectionCard title={t('peso.secQualifications', 'IV. Qualification Requirements')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('peso.workExpMonths', 'Work Experience (month/s)')}><Txt type="number" min="0" value={f.work_experience_months} onChange={(v) => set('work_experience_months', v)} /></Field>
            <Field label={t('peso.religion', 'Religion')}><Txt value={f.religion} onChange={(v) => set('religion', v)} /></Field>
          </div>
          <Field label={t('peso.sexPref', 'Sex')}>
            <RadioRow name="sexpref" value={f.sex_pref} onChange={(v) => set('sex_pref', v)} options={SEX_PREF} />
          </Field>
          <Field label={t('peso.civilStatusPref', 'Civil Status')}>
            <RadioRow name="cspref" value={f.civil_status_pref} onChange={(v) => set('civil_status_pref', v)} options={CIVIL_STATUS_PREF} />
          </Field>
          <Field label={t('peso.otherQualifications', 'Other qualifications')}>
            <textarea value={f.other_qualifications} onChange={(e) => set('other_qualifications', e.target.value)} rows={2}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </Field>
          <Field label={t('peso.acceptsDisability', 'Accepts Disability')} required>
            <RadioRow name="acceptsdis" value={f.accepts_disability} onChange={(v) => set('accepts_disability', v)} options={YES_NO} />
          </Field>
          {showDisabilityTypes && (
            <Field label={t('peso.disabilityTypes', 'If “yes”, specify')}>
              <CheckGroup value={f.accepts_disability_types} onChange={(v) => set('accepts_disability_types', v)} options={ACCEPTS_DISABILITY_TYPES} columns={3} />
            </Field>
          )}
          {showDisabilityOther && (
            <Field label={t('peso.disabilityOther', 'Others (please specify)')}><Txt value={f.accepts_disability_other} onChange={(v) => set('accepts_disability_other', v)} /></Field>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('peso.educationalLevel', 'Educational Level')}><Sel value={f.educational_level} onChange={(v) => set('educational_level', v)} options={NSRP_HIGHEST_EDUCATION} /></Field>
            <Field label={t('peso.courseMajor', 'Course/major')}><Txt value={f.course_major} onChange={(v) => set('course_major', v)} /></Field>
            <Field label={t('peso.license', 'License')}><Txt value={f.license} onChange={(v) => set('license', v)} /></Field>
            <Field label={t('peso.eligibility', 'Eligibility')}><Txt value={f.eligibility} onChange={(v) => set('eligibility', v)} /></Field>
            <Field label={t('peso.certification', 'Certification')}><Txt value={f.certification} onChange={(v) => set('certification', v)} /></Field>
            <Field label={t('peso.languagesSpoken', 'Language/dialect Spoken')}><Txt value={f.languages_spoken} onChange={(v) => set('languages_spoken', v)} /></Field>
            <Field label={t('peso.preferredResidence', 'Preferred Residence')}><Txt value={f.preferred_residence} onChange={(v) => set('preferred_residence', v)} /></Field>
          </div>
          <Field label={t('peso.acceptsPrograms', 'Accepts')}>
            <CheckGroup value={f.accepts_programs} onChange={(v) => set('accepts_programs', v)} options={ACCEPTS_PROGRAMS} columns={3} />
          </Field>
        </SectionCard>

        {/* V. Posting Details */}
        <SectionCard title={t('peso.secPosting', 'V. Posting Details')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('peso.postingDate', 'Posting Date (mm/dd/year)')}><Txt type="date" value={f.posting_date} onChange={(v) => set('posting_date', v)} min={todayStr} /></Field>
            <Field label={t('peso.validUntil', 'Valid Until (mm/dd/year)')}><Txt type="date" value={f.valid_until} onChange={(v) => set('valid_until', v)} min={f.posting_date || todayStr} /></Field>
          </div>
        </SectionCard>

        {/* Additional — not part of the official form */}
        <SectionCard title={t('peso.additionalSection', 'Additional (not part of the official form)')}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t('peso.facebook', 'FB / Messenger link')}><Txt value={f.facebook} onChange={(v) => set('facebook', v)} placeholder="https://facebook.com/..." /></Field>
          </div>
          <Field label={t('peso.companyDesc', 'Brief company description')} hint={t('peso.companyDescHint', 'e.g. A leading resort in Panglao specializing in eco-tourism.')}>
            <textarea value={f.business_description} onChange={(e) => set('business_description', e.target.value)} rows={2}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </Field>
          <FileField label={t('peso.logo', 'Establishment Official Logo (optional)')} file={logoFile} onPick={setLogoFile} hint={t('peso.logoHint', 'Image up to 5 MB')} />
          <FileField label={t('peso.poster', 'Job Hiring Poster (optional)')} file={posterFile} onPick={setPosterFile} hint={t('peso.posterHint', 'Image up to 5 MB')} />
          <Field label={t('peso.benefits', 'Benefits')}>
            <CheckGroup value={f.benefits} onChange={(v) => set('benefits', v)} options={COMMON_BENEFITS} columns={3} />
          </Field>
        </SectionCard>

        {/* Certification/Authorization */}
        <SectionCard title={t('peso.secCertification', 'Certification/Authorization')}>
          <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600 space-y-1">
            <p className="break-words"><strong>{t('peso.companyName', 'Establishment')}:</strong> {f.business_name}</p>
            <p><strong>{t('peso.jobTitle', 'Position')}:</strong> {f.title} · {f.positions_available} {t('peso.vacancyN', 'vacancy(ies)')}</p>
            <p><strong>{t('peso.jobCategory', 'Category')}:</strong> {f.panglao_category}</p>
            <p className="break-all"><strong>{t('peso.designatedEmail', 'Login email')}:</strong> {f.email}</p>
          </div>
          <p className="text-[11px] text-slate-500">{t('peso.employerCreditNote', 'Creating your account and posting this PESO vacancy is free.')}</p>
          <label className="flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-slate-700">
            <input type="checkbox" checked={f.consent} onChange={(e) => set('consent', e.target.checked)} className="mt-0.5 h-4 w-4 accent-blue-600" />
            <span>{t('peso.employerCertify', CERT_FALLBACK)}</span>
          </label>
        </SectionCard>
      </div>

      <div className="mt-5 flex justify-end">
        <Button onClick={handleSubmit} disabled={submitting || !f.consent} className="bg-blue-700 hover:bg-blue-800">
          {submitting ? <><Loader2 size={16} className="mr-1 animate-spin" /> {t('peso.submitting', 'Submitting…')}</> : t('peso.employerSubmit', 'Register & Post Vacancy')}
        </Button>
      </div>
      </PesoLayout>
    </>
  );
};

export default PesoEmployerRegister;
